import React, { useState } from "react";
import { Dialog, Divider, CircularProgress } from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import styled from "styled-components";
import { CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import toast from "react-hot-toast";

import SoftBox from "../SoftBox";
import SoftTypography from "../SoftTypography";
import SoftButton from "../SoftButton";
import { ADD_NEW_DEFAULT_CARD } from "../../api/payment";

const CardInputWrapper = styled.div`
  border: 1px solid #e4e7ec;
  background: #f6f7f9;
  border-radius: 8px;
  padding: 15px 10px;
  margin-bottom: 20px;
`;

const AddNewCardDialog = ({ newCardDialog, closeNewCardDialog }) => {
  const [loading, setLoading] = useState(false);

  const stripe = useStripe();
  const elements = useElements();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (!stripe || !elements) {
      return;
    }

    try {
      const cardElement = elements.getElement(CardElement);
      const { token, error } = await stripe.createToken(cardElement);

      if (error) {
        toast.error(error.message);
        setLoading(false);
        return;
      }

      const data = {
        cardToken: token.id,
      };
      await ADD_NEW_DEFAULT_CARD(data);

      toast.success("Cards added successfully");
      setLoading(false);
      closeNewCardDialog();
    } catch (error) {
      console.log("Error creating a new default card", error.response);
      toast.error(error.response.data.message ?? "Unable to add a card");
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={newCardDialog}
      onClose={closeNewCardDialog}
      sx={{
        ".MuiPaper-root": {
          padding: { mini: "1rem", md: "1rem 1.5rem" },
          borderRadius: "24px",
          background:
            "linear-gradient(180deg, rgba(255,255,255,0.70) 0%, rgba(255,255,255,0.60) 100%)",
          boxShadow:
            "0px 24px 48px -8px rgba(16,24,40,0.18), inset 0px 1px 0px 0px rgba(255,255,255,0.7)",
          backdropFilter: "blur(18px) saturate(160%)",
          width: "100%",
          maxWidth: "600px",
          border: "1px solid rgba(255,255,255,0.6)",
        },
      }}
    >
      <SoftBox
        display="flex"
        justifyContent="space-between"
        alignItems="center"
      >
        <SoftTypography variant="h4" sx={{ fontSize: "20px", color: "#0F172A" }}>
          Add and Replace card
        </SoftTypography>
        <SoftButton
          variant="outlined"
          onClick={closeNewCardDialog}
          sx={{
            "& svg": { fontSize: "1.25rem !important" },
            borderRadius: "50%",
            minWidth: "max-content",
            minHeight: "max-content",
            padding: "0.25rem",
            color: "#475467",
          }}
        >
          <CloseRoundedIcon />
        </SoftButton>
      </SoftBox>
      <Divider
        sx={{
          backgroundColor: "rgba(15,23,42,0.10)",
        }}
      />
      <form onSubmit={handleSubmit}>
        <CardInputWrapper>
          <CardElement
            options={{
              hidePostalCode: true,
              style: {
                base: {
                  fontSize: "18px",
                  color: "#1d2939",
                  iconColor: "#475467",
                  "::placeholder": {
                    color: "#98A2B3",
                  },
                },
                invalid: {
                  color: "#9e2146",
                },
              },
            }}
          />
        </CardInputWrapper>
        <SoftBox display="flex" justifyContent="flex-end">
          <SoftButton
            type="submit"
            variant="outlined"
            disabled={loading}
            sx={{
              color: "#475467",
              border: "1px solid rgba(15,23,42,0.10)",
              background: "rgba(15,23,42,0.03)",
              "&:hover": { background: "rgba(15,23,42,0.06)" },
              textTransform: "none",
              borderRadius: "4px",
            }}
          >
            Add Card
            {loading && (
              <CircularProgress color="inherit" size="1rem" className="ml-2" />
            )}
          </SoftButton>
        </SoftBox>
      </form>
    </Dialog>
  );
};

export default AddNewCardDialog;
